// City tags (Ben, Oct 8 2026): a player's own labels for their cities, one book per world — the tags (name + colour) and
// which cities wear which (up to five each). The game has nowhere to keep them, so the Worker stores the book in Cloudflare
// KV under the signed-in player (worker/tags.ts). This file is shared by the Worker and the app: no imports, no DOM.

export const TAG_COLORS = {
  gold: "#D9B86C",
  amber: "#E0913A",
  red: "#CF4B3C",
  green: "#5BA67A",
  steel: "#8FA9C0",
  violet: "#9B7FD1",
} as const;
export type TagColor = keyof typeof TAG_COLORS;
export const TAG_COLOR_KEYS = Object.keys(TAG_COLORS) as TagColor[];

export const MAX_TAGS_PER_CITY = 5;
export const MAX_TAGS = 24;
export const TAG_NAME_MAX = 20;
const MAX_CITIES = 500;

export interface CityTag {
  id: string;
  name: string;
  color: TagColor;
}

export interface TagBook {
  tags: CityTag[];
  /** city id → tag ids, in the order they were put on */
  cities: Record<string, string[]>;
}

export const emptyBook = (): TagBook => ({ tags: [], cities: {} });

/** A tag name as stored: trimmed, inner whitespace collapsed, at most TAG_NAME_MAX characters. */
export const cleanName = (s: unknown): string => (typeof s === "string" ? s.replace(/\s+/g, " ").trim().slice(0, TAG_NAME_MAX) : "");

/** Anything → a valid book: unknown colours become gold, duplicate names (case-insensitive) and ids are dropped, cities
 * keep only tags that exist (no repeats, at most five), empty cities are left out. Used on both sides of the wire. */
export function cleanBook(x: unknown): TagBook {
  const out = emptyBook();
  if (!x || typeof x !== "object") return out;
  const raw = x as { tags?: unknown; cities?: unknown };
  const ids = new Set<string>();
  const names = new Set<string>();
  if (Array.isArray(raw.tags)) {
    for (const t of raw.tags) {
      if (out.tags.length >= MAX_TAGS) break;
      if (!t || typeof t !== "object") continue;
      const r = t as { id?: unknown; name?: unknown; color?: unknown };
      const id = typeof r.id === "string" ? r.id.slice(0, 16) : "";
      const name = cleanName(r.name);
      if (!id || !/^[A-Za-z0-9_-]+$/.test(id) || !name || ids.has(id) || names.has(name.toLowerCase())) continue;
      const color = typeof r.color === "string" && r.color in TAG_COLORS ? (r.color as TagColor) : "gold";
      ids.add(id);
      names.add(name.toLowerCase());
      out.tags.push({ id, name, color });
    }
  }
  if (raw.cities && typeof raw.cities === "object" && !Array.isArray(raw.cities)) {
    let n = 0;
    for (const [vid, list] of Object.entries(raw.cities as Record<string, unknown>)) {
      if (n >= MAX_CITIES) break;
      if (!/^\d{1,12}$/.test(vid) || !Array.isArray(list)) continue;
      const kept: string[] = [];
      for (const id of list) if (typeof id === "string" && ids.has(id) && !kept.includes(id) && kept.length < MAX_TAGS_PER_CITY) kept.push(id);
      if (kept.length) {
        out.cities[vid] = kept;
        n++;
      }
    }
  }
  return out;
}

/** The tags a city wears, in order. */
export const tagsOf = (book: TagBook, vid: number | string): CityTag[] =>
  (book.cities[String(vid)] ?? []).map((id) => book.tags.find((t) => t.id === id)).filter((t): t is CityTag => !!t);

/** How many cities wear a tag. */
export const citiesWith = (book: TagBook, id: string): number => Object.values(book.cities).filter((l) => l.includes(id)).length;

/** A fresh tag id (short, URL-safe). */
export const newTagId = (): string => Math.random().toString(36).slice(2, 10);

/** Why a new tag name can't be used — null when it can. */
export function nameProblem(book: TagBook, name: string, exceptId?: string): string | null {
  const n = cleanName(name);
  if (!n) return "Give the tag a name";
  if (book.tags.some((t) => t.id !== exceptId && t.name.toLowerCase() === n.toLowerCase())) return "You already have a tag with that name";
  if (!exceptId && book.tags.length >= MAX_TAGS) return `Up to ${MAX_TAGS} tags in a world`;
  return null;
}

/** Puts a tag on a city or takes it off; false when the city already wears five. */
export function toggleTag(book: TagBook, vid: number | string, id: string): TagBook | false {
  const key = String(vid);
  const cur = book.cities[key] ?? [];
  if (cur.includes(id)) {
    const next = cur.filter((x) => x !== id);
    const cities = { ...book.cities };
    if (next.length) cities[key] = next;
    else delete cities[key];
    return { ...book, cities };
  }
  if (cur.length >= MAX_TAGS_PER_CITY) return false;
  return { ...book, cities: { ...book.cities, [key]: [...cur, id] } };
}

/** Deletes a tag and takes it off every city. */
export function deleteTag(book: TagBook, id: string): TagBook {
  const cities: Record<string, string[]> = {};
  for (const [vid, list] of Object.entries(book.cities)) {
    const next = list.filter((x) => x !== id);
    if (next.length) cities[vid] = next;
  }
  return { tags: book.tags.filter((t) => t.id !== id), cities };
}
